import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { FileText, Tag } from 'lucide-react';
import { useSaveTheme } from './hooks.ts';
import { useAutosave } from './useAutosave.ts';
import { AutosaveStatus } from './AutosaveStatus.tsx';
import { withMenu } from './menu.ts';
import type { MenuRow } from './menu.ts';
import type { SiteBundle } from './api.ts';

// Builder controls for one menu section: rename its menu label (per language),
// and jump to its content in the Content Manager. Structure/appearance only —
// the actual page copy lives in the Content Manager.
export function MenuSectionPanel({ bundle, row }: { bundle: SiteBundle; row: MenuRow }): JSX.Element {
  const save = useSaveTheme();
  const theme = bundle.site.theme ?? {};
  const [labels, setLabels] = useState<{ dv: string; en: string }>(() => ({
    dv: theme.menuLabels?.[row.id]?.dv ?? '',
    en: theme.menuLabels?.[row.id]?.en ?? '',
  }));
  const autosave = useAutosave((v: { dv: string; en: string }) => {
    const nextLabels = { ...(theme.menuLabels ?? {}) };
    const entry: { dv?: string; en?: string } = {};
    if (v.dv.trim()) entry.dv = v.dv.trim();
    if (v.en.trim()) entry.en = v.en.trim();
    if (entry.dv || entry.en) nextLabels[row.id] = entry;
    else delete nextLabels[row.id];
    return save.mutateAsync({ theme: withMenu(theme, { menuLabels: nextLabels }) });
  });

  const set = (k: 'dv' | 'en', v: string): void =>
    setLabels((prev) => {
      const next = { ...prev, [k]: v };
      autosave.schedule(next);
      return next;
    });

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <h2 className="font-serif text-2xl tracking-tight text-ink">{row.labelEn}</h2>
          <p className="thaana-display text-lg text-muted-foreground" dir="rtl">{row.labelDv}</p>
        </div>
        <AutosaveStatus status={autosave.status} />
      </header>

      <section className="space-y-3">
        <h3 className="section-heading font-serif text-lg text-ink flex items-center gap-2"><Tag size={17} className="text-primary" /> Menu label</h3>
        <div className="card-paper p-4 space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <label htmlFor="mlabel-dv" className="status-label">Menu label — Dhivehi</label>
              <input id="mlabel-dv" dir="rtl" className="input thaana" placeholder={row.labelDv} value={labels.dv} onChange={(e) => set('dv', e.target.value)} />
            </div>
            <div className="space-y-1">
              <label htmlFor="mlabel-en" className="status-label">Menu label — English</label>
              <input id="mlabel-en" dir="ltr" className="input" placeholder={row.labelEn} value={labels.en} onChange={(e) => set('en', e.target.value)} />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Overrides how this section is named in the site menu. Leave blank to use the standard title.
            {row.mandated ? '' : ' Toggle this optional section on/off in Settings.'}
          </p>
        </div>
      </section>

      {row.mandated && (
        <section className="space-y-2">
          <h3 className="section-heading font-serif text-lg text-ink">Page content</h3>
          <div className="card-paper p-4 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground">Edit this page's text and documents in the Content Manager.</p>
            <Link to="/content" search={{ s: row.id }} className="btn-secondary btn-sm inline-flex items-center gap-1.5">
              <FileText size={14} /> Edit content
            </Link>
          </div>
        </section>
      )}
    </div>
  );
}
